/**
 * 指挥页地图初始化视角 / 地球自转（从 Dashboard 拆出）
 */

import { getStyleModeFromBasemapId, ensureMandatoryOverlays } from '../mapProviderSettings';
import { isPlausibleCommandCoordinate, SAFE_COMMAND_CENTER } from '../bogusGps';
import { setActiveBasemapIdForCoord, toMapLngLat } from './basemapCoord';
import { isEarthSpinRenderPaused, isDashboardEarthDismissed, markDashboardEarthDismissed, clearDashboardEarthDismissed } from './earthSpinGate';
import { calculateZoomForHeight, COMMAND_VIEW_HEIGHT_METERS } from './mapFlyHelpers';
import { applyEarthDeepSpaceLook, applyEarthSpaceBackground, whenMapStyleSettled } from './earthDeepSpace';
import { notifyMemberMarkersProjectionReady } from './updateMemberMarkersOnMap';
import { restoreMapTerrain3d } from './mapTerrainHelpers';

/** 地球全览固定视角（与自转锁定一致） */
export const EARTH_OVERVIEW_CENTER = [0, 20];
export const EARTH_OVERVIEW_ZOOM = 1;

/** 自转过程中记住经度，避免 setStyle 后被多次拉回 0° 造成“往回跳” */
let rememberedEarthSpinLng = EARTH_OVERVIEW_CENTER[0];

export function rememberEarthSpinLng(lng) {
  const n = Number(lng);
  if (Number.isFinite(n)) rememberedEarthSpinLng = n;
}

export function getRememberedEarthSpinLng() {
  return rememberedEarthSpinLng;
}

function readProjectionName(map) {
  try {
    const proj = map?.getProjection?.();
    return typeof proj === 'string' ? proj : proj?.name;
  } catch (_) {
    return null;
  }
}

/**
 * 强制地球球体投影 + 全景高度。
 * 默认保留当前/记忆中的经度，切勿在自转中反复 jumpTo(0,20)。
 */
export function ensureEarthGlobeView(map, options = {}) {
  if (!map) return false;
  if (isDashboardEarthDismissed() && options.forceJump !== true) {
    return false;
  }
  const zoom = options.zoom ?? EARTH_OVERVIEW_ZOOM;
  const lat = options.lat ?? EARTH_OVERVIEW_CENTER[1];
  const preserveLng = options.preserveLng !== false;
  const forceJump = options.forceJump === true;

  let lng = options.lng;
  if (lng == null && preserveLng) {
    try {
      const currentLng = map.getCenter()?.lng;
      lng = Number.isFinite(Number(currentLng)) ? Number(currentLng) : rememberedEarthSpinLng;
    } catch (_) {
      lng = rememberedEarthSpinLng;
    }
  }
  if (lng == null || !Number.isFinite(Number(lng))) {
    lng = EARTH_OVERVIEW_CENTER[0];
  }
  rememberEarthSpinLng(lng);

  try {
    if (typeof map.setProjection === 'function' && readProjectionName(map) !== 'globe') {
      map.setProjection('globe');
    }
  } catch (_) {
    /* 旧版 mapbox-gl 可能不支持 */
  }

  // 切样式暂停期禁止 jumpTo（fog.state 未就绪会崩溃）；深空 fog 由 applyBasemap idle 后统一施加
  if (isEarthSpinRenderPaused() && !forceJump) {
    return false;
  }

  try {
    const center = map.getCenter?.();
    const curZoom = map.getZoom?.();
    const lngDrift = center ? Math.abs((((Number(center.lng) - lng) + 540) % 360) - 180) : 999;
    const latDrift = center ? Math.abs(Number(center.lat) - lat) : 999;
    const zoomDrift = Number.isFinite(curZoom) ? Math.abs(curZoom - zoom) : 999;
    // 已接近目标时不 jumpTo，减少切图源时的顿挫
    if (!forceJump && lngDrift < 0.8 && latDrift < 0.8 && zoomDrift < 0.12) {
      return true;
    }
    // 带标记事件，避免 moveend 触发海拔/存档风暴
    map.jumpTo({ center: [lng, lat], zoom }, { zhxtEarthSpin: true });
  } catch (_) {
    /* ignore */
  }
  return true;
}

/** 取消尚未执行的地球自转调度，避免同一次切样式触发多次回跳 */
let earthSpinScheduleToken = 0;
const earthSpinTimerIds = [];
let earthSpinIdleHandler = null;
let earthSpinIdleMap = null;

function clearEarthSpinSchedule() {
  earthSpinScheduleToken += 1;
  while (earthSpinTimerIds.length) {
    clearTimeout(earthSpinTimerIds.pop());
  }
  if (earthSpinIdleMap && earthSpinIdleHandler) {
    try {
      earthSpinIdleMap.off('idle', earthSpinIdleHandler);
    } catch (_) {
      /* ignore */
    }
  }
  earthSpinIdleHandler = null;
  earthSpinIdleMap = null;
}

/**
 * 判断本次初始化是否使用地球全览
 * - F5 / 强刷新 / 新开指挥页 → 地球自转（产品预期）
 * - 首页「先转地球」或同页仍在自转中 → 地球
 * - 同页内二次挂载（非刷新）→ 恢复上次视野，避免 Strict Mode 重复转球
 */
export function resolveShouldUseEarthOverview({
  isFreshPageLoad,
  forceEarthOverview,
  resumeEarthSpinning
}) {
  if (forceEarthOverview || resumeEarthSpinning) return true;
  if (isFreshPageLoad) return true;
  return false;
}

/**
 * 读取会话标记并决定是否地球全览
 * 注意：不要在此处写入 __zhxtDashboardMapSessionStarted。
 * 必须等地图真正创建成功后再 markDashboardMapSessionStarted()，
 * 否则 React Strict Mode 首次挂载被卸载后，二次挂载会误判为“非刷新”而跳过地球自转。
 */
export function readEarthOverviewSessionFlags(win = typeof window !== 'undefined' ? window : null) {
  const isFreshPageLoad = !(win && win.__zhxtDashboardMapSessionStarted);

  let forceEarthOverview = false;
  let resumeEarthSpinning = false;
  let hasEverVisitedDashboard = false;
  try {
    forceEarthOverview = sessionStorage.getItem('showEarthAnimation') === 'true';
    resumeEarthSpinning = sessionStorage.getItem('dashboardEarthSpinning') === 'true';
    hasEverVisitedDashboard = localStorage.getItem('dashboardEverVisited') === 'true';
    // 强刷新 / 新开页：除非首页主动「再转地球」，一律丢掉残留自转标记，避免白球卡死
    if (isFreshPageLoad && !forceEarthOverview && resumeEarthSpinning) {
      sessionStorage.removeItem('dashboardEarthSpinning');
      resumeEarthSpinning = false;
    }
  } catch (_) {
    /* ignore */
  }

  const shouldUseEarthOverview = resolveShouldUseEarthOverview({
    isFreshPageLoad,
    forceEarthOverview,
    resumeEarthSpinning
  });

  return {
    isFreshPageLoad,
    forceEarthOverview,
    resumeEarthSpinning,
    hasEverVisitedDashboard,
    shouldUseEarthOverview
  };
}

/** 地图就绪后短暂禁止「空白点击自动飞」，避免刷新误触飞向队员 */
export function markDashboardAutoFlySuppressed(ms = 10000, win = typeof window !== 'undefined' ? window : null) {
  if (!win) return;
  win.__zhxtSuppressDashboardAutoFlyUntil = Date.now() + Math.max(0, Number(ms) || 0);
}

export function isDashboardAutoFlySuppressed(win = typeof window !== 'undefined' ? window : null) {
  if (!win) return false;
  return Date.now() < Number(win.__zhxtSuppressDashboardAutoFlyUntil || 0);
}

/** 地图实例创建成功后再标记，避免 Strict Mode 双挂载吃掉“首次进入” */
export function markDashboardMapSessionStarted(win = typeof window !== 'undefined' ? window : null) {
  if (win) win.__zhxtDashboardMapSessionStarted = true;
}

// 地球离开标记：re-export，保持旧 import 路径可用
export {
  markDashboardEarthDismissed,
  clearDashboardEarthDismissed,
  isDashboardEarthDismissed
} from './earthSpinGate';

export function resolveInitialBasemapStyle({
  catalog = [],
  basemapId,
  overlayIds = [],
  providerSettings,
  buildRasterMapStyle,
  fallbackStyle = 'mapbox://styles/mapbox/satellite-streets-v12'
}) {
  const provider = catalog.find((item) => item.id === basemapId);
  const style = provider?.type === 'raster'
    ? buildRasterMapStyle(basemapId, overlayIds, providerSettings)
    : (provider?.styleUrl || fallbackStyle);
  return { provider, style };
}

const FALLBACK_COMMAND_VIEW = { center: [...SAFE_COMMAND_CENTER], zoom: 10 };

export { FALLBACK_COMMAND_VIEW };

/** 指挥视野存档是否可用（排除地球全景残留 / 境外伪定位 / 缺 zoom） */
export function isUsableCommandMapRestoreState(state) {
  if (!state || !Array.isArray(state.center) || state.center.length < 2) return false;
  const lng = Number(state.center[0]);
  const lat = Number(state.center[1]);
  const zoom = Number(state.zoom);
  if (!Number.isFinite(lng) || !Number.isFinite(lat) || !Number.isFinite(zoom)) return false;
  // zoom 过低 = 上次卡在地球全景；刷新若再恢复会得到「白色不转的球」
  if (zoom <= 2.5) return false;
  // 必须在中国业务范围内，避免 Chrome 网络定位把存档写成美国
  if (!isPlausibleCommandCoordinate(lat, lng)) return false;
  return true;
}

export function buildInitialMapViewState({
  shouldUseEarthOverview,
  resumeEarthSpinning,
  savedState,
  defaultState,
  initialStyle
}) {
  // 首次进入 / 强制地球 / 仍在自转中：一律地球全览，忽略上次“飞到队员”后的高缩放存档
  const useEarth = Boolean(shouldUseEarthOverview || resumeEarthSpinning);

  if (useEarth) {
    const resumeCenter = resumeEarthSpinning && savedState?.center ? savedState.center : EARTH_OVERVIEW_CENTER;
    return {
      center: [Number(resumeCenter[0]) || 0, EARTH_OVERVIEW_CENTER[1]],
      zoom: EARTH_OVERVIEW_ZOOM,
      style: initialStyle,
      mode: 'earth'
    };
  }

  const fallback = (defaultState && isUsableCommandMapRestoreState(defaultState))
    ? defaultState
    : FALLBACK_COMMAND_VIEW;
  let restoreState = isUsableCommandMapRestoreState(savedState) ? savedState : fallback;
  if (!isUsableCommandMapRestoreState(savedState) && savedState) {
    console.warn('丢弃无效地图存档（地球残留/伪定位/缺字段）', savedState);
  }
  return {
    center: [Number(restoreState.center[0]), Number(restoreState.center[1])],
    zoom: Number(restoreState.zoom),
    style: initialStyle,
    mode: 'restore'
  };
}

export function markEarthOverviewInitialized() {
  try {
    localStorage.setItem('dashboardEverVisited', 'true');
    sessionStorage.removeItem('showEarthAnimation');
  } catch (_) {
    /* ignore */
  }
}

/**
 * 创建地球自转控制器
 * @returns {{ start: Function, stop: Function, isActive: () => boolean }}
 */
export function createEarthSpinController(options = {}) {
  const {
    getMap,
    animationRef,
    isActiveRef,
    onActiveChange,
    degreesPerSecond = 1.8
  } = options;

  const start = () => {
    if (isDashboardEarthDismissed()) {
      console.log('用户已离开地球全景，跳过启动自转');
      return false;
    }
    const map = getMap?.();
    if (!map) {
      console.log('地图实例不存在，无法启动动画');
      return false;
    }
    if (isActiveRef?.current) {
      console.log('动画已经在运行，跳过启动');
      return false;
    }

    console.log('✅ 开始地球自转动画');
    // 先置位再通知 UI，避免 setState 重渲染过程中被误判为未启动
    if (isActiveRef) isActiveRef.current = true;
    onActiveChange?.(true);

    try {
      sessionStorage.setItem('dashboardEarthSpinning', 'true');
    } catch (_) {
      /* ignore */
    }

    let lastTime = 0;
    // 自转期间锁定球体投影 + 全景高度；经度沿用当前/记忆值，禁止每次拉回 0°
    const fixedLat = EARTH_OVERVIEW_CENTER[1];
    const fixedZoom = EARTH_OVERVIEW_ZOOM;
    ensureEarthGlobeView(map, {
      lng: map.getCenter()?.lng ?? rememberedEarthSpinLng,
      lat: fixedLat,
      zoom: fixedZoom,
      preserveLng: true
    });

    const animate = (currentTime) => {
      const liveMap = getMap?.();
      if (!liveMap || !isActiveRef?.current) return;

      // setStyle 过渡期不要 jumpTo，否则 Mapbox fog.getOpacityAtLatLng 会读到 undefined.state
      if (isEarthSpinRenderPaused()) {
        lastTime = 0;
        if (animationRef) animationRef.current = requestAnimationFrame(animate);
        return;
      }

      if (!lastTime) {
        lastTime = currentTime;
        if (animationRef) animationRef.current = requestAnimationFrame(animate);
        return;
      }

      const deltaTime = Math.min(currentTime - lastTime, 48);
      lastTime = currentTime;

      // 优先用记忆经度递推，避免 setStyle 瞬间 getCenter 抖动导致来回跳
      let nextLng = rememberedEarthSpinLng + degreesPerSecond * (deltaTime / 1000);
      if (nextLng > 180) nextLng -= 360;
      if (nextLng < -180) nextLng += 360;
      rememberEarthSpinLng(nextLng);

      // 仅在投影丢失时纠正，避免每帧 setProjection 造成闪烁
      try {
        if (readProjectionName(liveMap) !== 'globe' && typeof liveMap.setProjection === 'function') {
          liveMap.setProjection('globe');
        }
      } catch (_) {
        /* ignore */
      }

      try {
        // eventData 供 Dashboard 识别：自转 jumpTo 不拉海拔、不写 localStorage
        liveMap.jumpTo(
          { center: [nextLng, fixedLat], zoom: fixedZoom },
          { zhxtEarthSpin: true }
        );
      } catch (_) {
        /* 切样式瞬间 fog/style 未就绪时可能抛错，忽略单帧 */
      }

      if (animationRef) animationRef.current = requestAnimationFrame(animate);
    };

    if (animationRef) animationRef.current = requestAnimationFrame(animate);
    return true;
  };

  const stop = ({ clearOverviewFlag = true } = {}) => {
    console.log('停止地球自转动画', { clearOverviewFlag });
    onActiveChange?.(false);
    if (isActiveRef) isActiveRef.current = false;
    if (animationRef?.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (clearOverviewFlag) {
      markDashboardEarthDismissed();
      try {
        sessionStorage.removeItem('dashboardEarthSpinning');
      } catch (_) {
        /* ignore */
      }
    }
  };

  return {
    start,
    stop,
    isActive: () => Boolean(isActiveRef?.current)
  };
}

/**
 * 样式就绪后：标记会话、渲染图层、调度地球自转
 * （失败不阻断自转）
 */
export function runDashboardMapStyleReady(options = {}) {
  const {
    getMap,
    mapStyleReadyRef,
    setMapLoadingState,
    updateMarkers,
    renderTracks,
    loadRescueData,
    loadPickPoints,
    drawingToolsActiveRef,
    membersRef,
    getMembers,
    flyToLatestMemberRef,
    earthAnimationRef,
    shouldUseEarthOverviewRef,
    isAnimationActiveRef,
    startEarthAnimationRef,
    stopEarthAnimation,
    settingsLoaded,
    trackModeApplied,
    fetchDevices
  } = options;

  console.log('地图样式加载完成，开始初始化');
  if (mapStyleReadyRef) mapStyleReadyRef.current = true;
  markDashboardMapSessionStarted();
  markDashboardAutoFlySuppressed(10000);
  setMapLoadingState?.('loaded');

  // 地球全览：清 fog 会变成「白球」；恢复视野：清 fog 避免 outdoors 自带雾效未就绪崩溃
  try {
    const mapEarly = getMap?.();
    if (mapEarly) {
      if (shouldUseEarthOverviewRef?.current) {
        whenMapStyleSettled(mapEarly, () => {
          applyEarthDeepSpaceLook(mapEarly, { allowDuringPause: true });
        });
      } else if (typeof mapEarly.setFog === 'function') {
        mapEarly.setFog(null);
      }
    }
  } catch (_) {
    /* ignore */
  }

  try {
    const membersList = typeof getMembers === 'function'
      ? getMembers()
      : (membersRef?.current || []);
    updateMarkers?.(membersList);
  } catch (error) {
    console.error('初始化标记失败:', error);
  }
  try {
    renderTracks?.();
  } catch (error) {
    console.error('初始化轨迹失败:', error);
  }
  try {
    loadRescueData?.();
    loadPickPoints?.();
  } catch (error) {
    console.error('初始化救援/标点失败:', error);
  }

  // 仅地球全览模式才绑定「点空白飞指挥员」；常规恢复视野不绑，杜绝刷新误飞
  const mapForClick = getMap?.();
  const wantEarthClick = Boolean(shouldUseEarthOverviewRef?.current);
  if (mapForClick && wantEarthClick) {
    bindEarthBlankClickToCommanderFly(mapForClick, {
      drawingToolsActiveRef,
      shouldUseEarthOverviewRef,
      isAnimationActiveRef,
      earthAnimationRef,
      flyToLatestMemberRef,
      membersRef
    });
  }

  scheduleEarthSpinAfterStyleLoad({
    getMap,
    shouldUseEarthOverviewRef,
    isAnimationActiveRef,
    startEarthAnimationRef,
    stopEarthAnimation
  });

  setTimeout(() => {
    const map = getMap?.();
    if (!map) return;
    map.resize();
    console.log('地图初始化完成，已触发resize');
    if ((membersRef?.current || []).length === 0) {
      console.log('地图初始化完成，尝试加载队员数据...');
      setTimeout(() => {
        if (!settingsLoaded || !trackModeApplied) {
          console.log('使用基本API调用加载队员数据');
          fetchDevices?.(0);
        }
      }, 1000);
    }
  }, 100);
}

/**
 * 静默暂停自转帧（不 setState、不清理地球全览标记）。
 * 按下时若走完整 stopEarthAnimation → React 重渲染，Mapbox 常会丢掉 mouseup，导致“点了没反应”。
 */
export function pauseEarthSpinFramesSilent(options = {}) {
  const { animationRef, isActiveRef } = options;
  clearEarthSpinSchedule();
  if (isActiveRef) isActiveRef.current = false;
  if (animationRef?.current != null) {
    try { cancelAnimationFrame(animationRef.current); } catch (_) { /* ignore */ }
    animationRef.current = null;
  }
}

/**
 * 地球全览/自转时：点空白飞到指挥员上次定位。
 * - 自转 jumpTo 会吞掉 click，须先静默停帧
 * - 用 window pointerup（capture）完成点击，避免 React 重渲染丢事件
 * - 以 mousedown 时的「武装」标记判定，不依赖 pause 后的 ref 状态
 */
export function bindEarthBlankClickToCommanderFly(map, options = {}) {
  if (!map || map.__zhxtEarthBlankClickBound) return () => {};
  map.__zhxtEarthBlankClickBound = true;

  const {
    drawingToolsActiveRef,
    shouldUseEarthOverviewRef,
    isAnimationActiveRef,
    earthAnimationRef,
    flyToLatestMemberRef,
    membersRef,
    moveThresholdPx = 10
  } = options;

  const isEarthMode = () => {
    if (shouldUseEarthOverviewRef?.current || isAnimationActiveRef?.current) return true;
    // 被底图切换拽回全景后 ref 可能已清，仍按低缩放视为地球以便再次点击进入
    try {
      return Number(map.getZoom?.()) <= 2.5;
    } catch (_) {
      return false;
    }
  };

  /** @type {{ x: number, y: number, armed: boolean } | null} */
  let pointerDown = null;
  let flyLock = false;

  const clearPointer = () => {
    pointerDown = null;
  };

  const isBlocked = () => Boolean(
    drawingToolsActiveRef?.current
    || map.__zhxtBlockEarthBlankFly
  );

  const tryFly = (clientX, clientY) => {
    if (!pointerDown?.armed || flyLock) {
      clearPointer();
      return;
    }
    const dx = clientX - pointerDown.x;
    const dy = clientY - pointerDown.y;
    const armed = pointerDown.armed;
    clearPointer();
    if (!armed || isBlocked()) return;
    if (Math.hypot(dx, dy) > moveThresholdPx) return;
    if (!isEarthMode()) return;
    // 用户主动点击必须生效：不再受「刷新防误飞」窗口拦截（该窗口曾导致要点多次）

    flyLock = true;
    console.log('地球模式下点击地图，飞行到指挥员上次定位', {
      membersCount: (membersRef?.current || []).length
    });
    try {
      flyToLatestMemberRef?.current?.();
    } finally {
      // 短锁，防止 pointerup + click 双触发
      setTimeout(() => { flyLock = false; }, 800);
    }
  };

  const onMouseDown = (e) => {
    if (isBlocked()) {
      clearPointer();
      return;
    }
    if (!isEarthMode()) {
      clearPointer();
      return;
    }
    const oe = e.originalEvent;
    pointerDown = {
      x: oe?.clientX ?? e.point?.x ?? 0,
      y: oe?.clientY ?? e.point?.y ?? 0,
      armed: true
    };
    // 只停 rAF，不触发 setState，避免抬起事件丢失
    pauseEarthSpinFramesSilent({
      animationRef: earthAnimationRef,
      isActiveRef: isAnimationActiveRef
    });
  };

  const onWindowPointerUp = (e) => {
    if (!pointerDown?.armed) return;
    tryFly(e.clientX, e.clientY);
  };

  // click 兜底：仅当本次有武装的按下（pointerDown.armed）时才飞，
  // 避免样式加载/标记点击等「裸 click」误触发突然飞向队员
  const onMapClick = () => {
    if (isBlocked() || flyLock) return;
    if (!pointerDown?.armed) return;
    tryFly(pointerDown.x, pointerDown.y);
  };

  map.on('mousedown', onMouseDown);
  map.on('click', onMapClick);
  if (typeof window !== 'undefined') {
    window.addEventListener('pointerup', onWindowPointerUp, true);
    window.addEventListener('mouseup', onWindowPointerUp, true);
  }
  console.log('地图空白点击监听已绑定（地球模式→指挥员定位，静默停转+window pointerup）');

  return () => {
    try {
      map.off('mousedown', onMouseDown);
      map.off('click', onMapClick);
      map.__zhxtEarthBlankClickBound = false;
    } catch (_) {
      /* ignore */
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('pointerup', onWindowPointerUp, true);
      window.removeEventListener('mouseup', onWindowPointerUp, true);
    }
  };
}

/**
 * 地球俯冲飞入：保持 globe 投影完成 flyTo，落地后再收成墨卡托并清雾。
 * （中途 setProjection('mercator') / setFog(null) 会造成「拍扁」感，观感远差于首页）
 */
export function flyFromEarthOverview(map, options = {}) {
  if (!map) return false;
  const {
    center,
    zoom,
    heightMeters = COMMAND_VIEW_HEIGHT_METERS,
    duration = 3200,
    easing = (t) => t * (2 - t),
    stopEarthAnimation,
    onComplete
  } = options;

  markDashboardEarthDismissed();
  try {
    stopEarthAnimation?.({ clearOverviewFlag: true });
  } catch (_) {
    /* ignore */
  }

  let lng = Number(center?.[0]);
  let lat = Number(center?.[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat) || !isPlausibleCommandCoordinate(lat, lng)) {
    console.warn('地球俯冲目标不可用，改落龙泉默认', lng, lat);
    lng = SAFE_COMMAND_CENTER[0];
    lat = SAFE_COMMAND_CENTER[1];
  }

  const screenH = map.getContainer?.()?.clientHeight
    || (typeof window !== 'undefined' ? window.innerHeight : 800);
  const z = Number.isFinite(Number(zoom))
    ? Number(zoom)
    : calculateZoomForHeight(heightMeters, lat, screenH);

  try {
    map.stop?.();
  } catch (_) {
    /* ignore */
  }

  // 中间不要切 mercator / 清 fog：让 Mapbox 在 globe 上完成从太空到地面的过渡
  try {
    map.flyTo({
      center: [lng, lat],
      zoom: z,
      duration,
      essential: true,
      easing
    });
  } catch (_) {
    return false;
  }

  schedulePostEarthDiveCleanup(map, { onComplete });
  return true;
}

/**
 * 飞入动画结束后再收投影与雾效（或超时兜底）
 */
export function schedulePostEarthDiveCleanup(map, options = {}) {
  if (!map) return () => {};
  const minZoom = options.minZoom ?? 4;
  const timeoutMs = options.timeoutMs ?? 9000;
  let done = false;

  const finish = () => {
    if (done) return;
    done = true;
    try {
      map.off?.('moveend', onMoveEnd);
    } catch (_) {
      /* ignore */
    }
    try {
      map.off?.('zoom', onZoom);
    } catch (_) {
      /* ignore */
    }
    clearTimeout(timer);
    try {
      if (typeof map.setProjection === 'function') {
        const name = readProjectionName(map);
        if (name === 'globe') map.setProjection('mercator');
      }
    } catch (_) {
      /* ignore */
    }
    try {
      if (typeof map.setFog === 'function') map.setFog(null);
    } catch (_) {
      /* ignore */
    }
    try {
      restoreMapTerrain3d(map);
    } catch (_) {
      /* ignore */
    }
    try {
      notifyMemberMarkersProjectionReady(map);
    } catch (_) {
      /* ignore */
    }
    try {
      options.onComplete?.();
    } catch (_) {
      /* ignore */
    }
  };

  const onMoveEnd = () => {
    try {
      if (map.isMoving?.()) return;
      if (Number(map.getZoom?.()) >= minZoom) finish();
    } catch (_) {
      /* ignore */
    }
  };

  // 俯冲途中 zoom 一过阈值就切 mercator + notify，避免非得等完整 flyTo/9s 超时才出人点
  const onZoom = () => {
    try {
      if (Number(map.getZoom?.()) >= minZoom) finish();
    } catch (_) {
      /* ignore */
    }
  };

  const timer = setTimeout(finish, timeoutMs);
  try {
    map.on('moveend', onMoveEnd);
  } catch (_) {
    /* ignore */
  }
  try {
    map.on('zoom', onZoom);
  } catch (_) {
    /* ignore */
  }
  return () => {
    done = true;
    clearTimeout(timer);
    try {
      map.off?.('moveend', onMoveEnd);
    } catch (_) {
      /* ignore */
    }
    try {
      map.off?.('zoom', onZoom);
    } catch (_) {
      /* ignore */
    }
  };
}

/**
 * 退出地球全景并落到可用指挥视野。
 * 默认忽略可能被伪定位污染的 map_state；仅当 preferSavedState=true 且存档在中国范围内才用存档。
 */
export function exitEarthToCommandView(options = {}) {
  const {
    getMap,
    stopEarthAnimation,
    savedState = null,
    defaultState = FALLBACK_COMMAND_VIEW,
    preferSavedState = false,
    duration = 3200
  } = options;

  const map = typeof getMap === 'function' ? getMap() : getMap;
  if (!map) {
    markDashboardEarthDismissed();
    try {
      stopEarthAnimation?.({ clearOverviewFlag: true });
    } catch (_) {
      /* ignore */
    }
    return false;
  }

  const fallback = isUsableCommandMapRestoreState(defaultState)
    ? defaultState
    : FALLBACK_COMMAND_VIEW;
  // 地球俯冲默认落龙泉，避免 map_state 被 Chrome 写成美国中心
  const target = (preferSavedState && isUsableCommandMapRestoreState(savedState))
    ? savedState
    : fallback;

  return flyFromEarthOverview(map, {
    center: [Number(target.center[0]), Number(target.center[1])],
    // 从地球进入：统一落到约 600m 离地，不用过低缩放存档
    heightMeters: COMMAND_VIEW_HEIGHT_METERS,
    duration,
    stopEarthAnimation
  });
}

/**
 * 地球全景卡住（白球不转）时：退出地球模式并落到指挥默认视野
 */
export function abandonStuckEarthOverview(options = {}) {
  const {
    getMap,
    shouldUseEarthOverviewRef,
    isAnimationActiveRef,
    stopEarthAnimation,
    defaultCenter = FALLBACK_COMMAND_VIEW.center,
    defaultZoom = FALLBACK_COMMAND_VIEW.zoom
  } = options;

  console.warn('地球自转未能启动，退出全景避免白球卡住');
  if (shouldUseEarthOverviewRef) shouldUseEarthOverviewRef.current = false;
  try {
    stopEarthAnimation?.({ clearOverviewFlag: true });
  } catch (_) {
    if (isAnimationActiveRef) isAnimationActiveRef.current = false;
  }

  const map = getMap?.();
  if (!map) return;
  try {
    if (typeof map.setFog === 'function') map.setFog(null);
  } catch (_) {
    /* ignore */
  }
  try {
    if (typeof map.setProjection === 'function') map.setProjection('mercator');
  } catch (_) {
    /* ignore */
  }
  try {
    map.jumpTo({ center: defaultCenter, zoom: defaultZoom });
  } catch (_) {
    /* ignore */
  }
  try {
    notifyMemberMarkersProjectionReady(map);
  } catch (_) {
    /* ignore */
  }
}

/** 样式就绪后尽快启动地球自转（idle / 超时兜底） */
export function scheduleEarthSpinAfterStyleLoad(options = {}) {
  const {
    getMap,
    shouldUseEarthOverviewRef,
    isAnimationActiveRef,
    startEarthAnimationRef,
    stopEarthAnimation
  } = options;

  const map = getMap?.();
  const shouldStartAnimation = Boolean(shouldUseEarthOverviewRef?.current)
    && !isDashboardEarthDismissed();
  console.log('地图缩放级别:', map?.getZoom?.(), '是否启动动画:', shouldStartAnimation, {
    shouldUseEarthOverview: shouldUseEarthOverviewRef?.current,
    earthDismissed: isDashboardEarthDismissed()
  });

  if (!shouldStartAnimation || !map) return;

  // 切图源会反复 style.load：取消旧调度，只保留最新一轮，避免 0° 回跳叠加
  clearEarthSpinSchedule();
  const token = earthSpinScheduleToken;
  let animationStarted = false;
  const waitStartedAt = Date.now();

  const beginSmoothSpin = () => {
    if (token !== earthSpinScheduleToken) return;
    const liveMap = getMap?.();
    if (!liveMap || !shouldUseEarthOverviewRef?.current || isDashboardEarthDismissed()) return;

    // 切底图过渡期先等闸门放开，避免 fog 未就绪时 jumpTo
    if (isEarthSpinRenderPaused()) {
      // 闸门异常长时间不放开时放弃地球，避免永久白球
      if (Date.now() - waitStartedAt > 5000) {
        abandonStuckEarthOverview({
          getMap,
          shouldUseEarthOverviewRef,
          isAnimationActiveRef,
          stopEarthAnimation
        });
        return;
      }
      earthSpinTimerIds.push(setTimeout(beginSmoothSpin, 200));
      return;
    }

    try {
      applyEarthDeepSpaceLook(liveMap, { allowDuringPause: true });
    } catch (_) {
      /* ignore */
    }

    // 保留自转经度，只纠正球体/高度；切勿 jumpTo(0,20)
    ensureEarthGlobeView(liveMap, {
      lng: getRememberedEarthSpinLng(),
      preserveLng: true,
      zoom: EARTH_OVERVIEW_ZOOM
    });

    if (isAnimationActiveRef?.current) {
      animationStarted = true;
      return;
    }
    if (animationStarted) return;
    animationStarted = true;
    startEarthAnimationRef?.current?.();

    // 启动后仍未转起来 → 退出全景，不要卡在白球
    earthSpinTimerIds.push(setTimeout(() => {
      if (token !== earthSpinScheduleToken) return;
      if (!shouldUseEarthOverviewRef?.current) return;
      if (isAnimationActiveRef?.current) return;
      abandonStuckEarthOverview({
        getMap,
        shouldUseEarthOverviewRef,
        isAnimationActiveRef,
        stopEarthAnimation
      });
    }, 2200));
  };

  earthSpinTimerIds.push(setTimeout(beginSmoothSpin, 280));
  earthSpinIdleMap = map;
  earthSpinIdleHandler = beginSmoothSpin;
  try {
    map.once('idle', beginSmoothSpin);
  } catch (_) {
    earthSpinIdleHandler = null;
    earthSpinIdleMap = null;
  }
  // 仅保留一次兜底，去掉 1.5s+3.5s 双重点名造成的二次回跳
  earthSpinTimerIds.push(setTimeout(beginSmoothSpin, 1600));
}

/** Strict Mode 卸载时清理会话标记 */
export function cleanupDashboardMapSessionOnUnmount(options = {}) {
  const {
    mapStyleReadyRef,
    stopEarthAnimation,
    clearRescueData,
    clearPickPoints,
    win = typeof window !== 'undefined' ? window : null
  } = options;

  clearRescueData?.();
  clearPickPoints?.();
  stopEarthAnimation?.({ clearOverviewFlag: false });

  if (!mapStyleReadyRef?.current && win) {
    try {
      delete win.__zhxtDashboardMapSessionStarted;
    } catch (_) {
      /* ignore */
    }
  }
  if (mapStyleReadyRef) mapStyleReadyRef.current = false;
}

/** 绑定 moveend/zoomend 持久化（自转中跳过） */
export function attachDashboardMapStatePersistence(map, options = {}) {
  if (!map) return () => {};
  const {
    isAnimationActiveRef,
    getStyleId,
    saveMapState
  } = options;

  const persist = () => {
    if (isAnimationActiveRef?.current) return;
    const center = map.getCenter();
    const zoom = map.getZoom();
    // 不把地球全景缩放写入存档，否则下次刷新会恢复成不转的白球
    if (!Number.isFinite(Number(zoom)) || Number(zoom) <= 2.5) return;
    saveMapState?.({
      center: [center.lng, center.lat],
      zoom,
      style: getStyleId?.()
    });
  };

  map.on('moveend', persist);
  map.on('zoomend', persist);
  return () => {
    map.off('moveend', persist);
    map.off('zoomend', persist);
  };
}

/**
 * 解析指挥页地图首次创建所需视角 / 底图 / 地球全览标记
 */
export function resolveDashboardMapBootstrapPlan(options = {}) {
  const {
    savedState,
    defaultState,
    currentBasemapId,
    providerSettings = {},
    activeOverlayIds = [],
    catalog = [],
    buildRasterMapStyle,
    fallbackStyle = 'mapbox://styles/mapbox/satellite-streets-v12'
  } = options;

  const flags = readEarthOverviewSessionFlags();
  const initialBasemapId = currentBasemapId || providerSettings.defaultBasemapId;
  const useGlobeStyle = Boolean(
    flags.shouldUseEarthOverview || flags.resumeEarthSpinning || flags.forceEarthOverview
  );
  const { provider: initialProvider, style: initialStyle } = resolveInitialBasemapStyle({
    catalog,
    basemapId: initialBasemapId,
    overlayIds: activeOverlayIds,
    providerSettings,
    buildRasterMapStyle: (id, overlays, settings) => buildRasterMapStyle(id, overlays, settings, {
      globe: useGlobeStyle
    }),
    fallbackStyle
  });
  const initialState = buildInitialMapViewState({
    shouldUseEarthOverview: flags.shouldUseEarthOverview,
    resumeEarthSpinning: flags.resumeEarthSpinning,
    savedState,
    defaultState,
    initialStyle
  });

  const useEarth = initialState.mode === 'earth';
  if (useEarth) {
    // 本页要重新转地球：清掉「已离开」标记
    clearDashboardEarthDismissed();
    console.log('使用地球视图初始化', {
      isFreshPageLoad: flags.isFreshPageLoad,
      forceEarthOverview: flags.forceEarthOverview,
      resumeEarthSpinning: flags.resumeEarthSpinning,
      hasEverVisitedDashboard: flags.hasEverVisitedDashboard,
      shouldUseEarthOverview: flags.shouldUseEarthOverview
    });
    markEarthOverviewInitialized();
  } else {
    // 恢复上次视野时也标记已访问，避免下次误判为首访
    markEarthOverviewInitialized();
    console.log('恢复指挥台上次视野', {
      zoom: initialState.zoom,
      center: initialState.center,
      savedState
    });
  }

  // 左上角按钮高亮用样式模式（streets/satellite/outdoors），不再写死 Mapbox URL
  const styleIdForUi = getStyleModeFromBasemapId(initialBasemapId);
  setActiveBasemapIdForCoord(initialBasemapId);

  return {
    flags,
    initialBasemapId,
    initialProvider,
    initialState,
    styleIdForUi,
    useEarth
  };
}

/** 创建 Mapbox 地图实例（隐藏 attribution） */
export function createDashboardMapboxInstance(mapboxgl, container, initialState) {
  if (!mapboxgl?.Map || !container || !initialState) return null;
  const isEarth = initialState.mode === 'earth';
  const map = new mapboxgl.Map({
    container,
    style: initialState.style,
    center: initialState.center,
    zoom: initialState.zoom,
    attributionControl: false,
    // 仅地球全览用球体；恢复指挥视野用墨卡托，避免刷新出现「不转的白球」
    projection: isEarth ? 'globe' : 'mercator'
  });
  if (isEarth) {
    map.once('style.load', () => {
      // 首屏也等 idle，避免官方样式 fog 未就绪时 jumpTo 崩溃
      try {
        applyEarthSpaceBackground(map);
      } catch (_) {
        /* ignore */
      }
      whenMapStyleSettled(map, () => {
        applyEarthDeepSpaceLook(map, { allowDuringPause: true });
        ensureEarthGlobeView(map, {
          lng: initialState.center?.[0],
          lat: initialState.center?.[1],
          zoom: initialState.zoom
        });
      });
    });
  }
  return map;
}

/**
 * 一次性创建指挥页地图实例并绑定 style.load / 状态持久化
 * @returns {{ ok: boolean, reason?: string }}
 */
export function bootstrapDashboardMapInstance(options = {}) {
  const {
    mapboxgl,
    container,
    mapRef,
    shouldUseEarthOverviewRef,
    isAnimationActiveRef,
    mapStyleReadyRef,
    drawingToolsActiveRef,
    membersRef,
    flyToLatestMemberRef,
    earthAnimationRef,
    startEarthAnimationRef,
    stopEarthAnimation,
    currentBasemapIdRef,
    mapProviderSettingsRef,
    activeOverlayIdsRef,
    catalog,
    buildRasterMapStyle,
    getSavedMapState,
    getDefaultMapState,
    saveMapState,
    getStyleId,
    setCurrentMapStyleId,
    setMapLoadingState,
    updateMarkers,
    renderTracks,
    loadRescueData,
    loadPickPoints,
    getMembers,
    settingsLoaded,
    trackModeApplied,
    fetchDevices
  } = options;

  if (mapRef?.current) return { ok: false, reason: 'already-ready' };
  if (!container) return { ok: false, reason: 'no-container' };

  const containerRect = container.getBoundingClientRect?.() || { width: 0, height: 0 };
  console.log('开始初始化地图，容器尺寸:', containerRect.width, 'x', containerRect.height);

  const {
    initialBasemapId,
    initialProvider,
    initialState,
    styleIdForUi,
    useEarth
  } = resolveDashboardMapBootstrapPlan({
    savedState: getSavedMapState?.(),
    defaultState: getDefaultMapState?.(),
    currentBasemapId: currentBasemapIdRef?.current,
    providerSettings: mapProviderSettingsRef?.current,
    activeOverlayIds: activeOverlayIdsRef?.current,
    catalog,
    buildRasterMapStyle
  });

  if (shouldUseEarthOverviewRef) {
    shouldUseEarthOverviewRef.current = useEarth;
  }

  try {
    const map = createDashboardMapboxInstance(mapboxgl, container, initialState);
    if (!map) return { ok: false, reason: 'create-failed' };
    mapRef.current = map;
    // 创建后立刻同步底图 id，避免定位时读到过期基准误做 GCJ 纠偏
    setActiveBasemapIdForCoord(initialBasemapId, map);
    if (currentBasemapIdRef) currentBasemapIdRef.current = initialBasemapId;
    console.log('地图实例创建成功');

    setCurrentMapStyleId?.(styleIdForUi || getStyleModeFromBasemapId(initialBasemapId));

    attachDashboardMapStatePersistence(map, {
      isAnimationActiveRef,
      getStyleId,
      saveMapState
    });

    map.on('style.load', () => {
      // 国界线必显：首屏 Mapbox 官方样式不经 applyBasemap，每次 setStyle 后也在此兜底补上
      try {
        ensureMandatoryOverlays(map, mapProviderSettingsRef?.current);
      } catch (_) {
        /* ignore */
      }
      runDashboardMapStyleReady({
        getMap: () => mapRef.current,
        mapStyleReadyRef,
        setMapLoadingState,
        updateMarkers,
        renderTracks,
        loadRescueData,
        loadPickPoints,
        drawingToolsActiveRef,
        membersRef,
        getMembers,
        flyToLatestMemberRef,
        earthAnimationRef,
        shouldUseEarthOverviewRef,
        isAnimationActiveRef,
        startEarthAnimationRef,
        stopEarthAnimation,
        settingsLoaded,
        trackModeApplied,
        fetchDevices
      });
    });

    return { ok: true, useEarth, initialBasemapId };
  } catch (error) {
    console.error('地图初始化失败:', error);
    return { ok: false, reason: 'exception', error };
  }
}

/**
 * 等待地图容器挂载且尺寸就绪后再回调
 * @returns {{ start: (delayMs?: number) => void, stop: () => void }}
 */
export function waitForMapContainerReady(options = {}) {
  const {
    getContainer,
    isMapReady,
    onReady,
    maxAttempts = 40,
    missingDelayMs = 100,
    zeroSizeDelayMs = 200,
    ResizeObserverImpl = typeof ResizeObserver !== 'undefined' ? ResizeObserver : null
  } = options;

  let initAttempts = 0;
  let stopped = false;
  let activeObserver = null;

  const initializeMap = () => {
    if (stopped || isMapReady?.()) return;

    const container = getContainer?.();
    if (!container) {
      initAttempts += 1;
      if (initAttempts <= maxAttempts) {
        console.log('地图容器尚未挂载，延迟重试初始化', initAttempts);
        setTimeout(initializeMap, missingDelayMs);
      }
      return;
    }

    const containerRect = container.getBoundingClientRect();
    if (containerRect.width === 0 || containerRect.height === 0) {
      console.log('地图容器尺寸为0，延迟200ms后重试初始化');
      setTimeout(initializeMap, zeroSizeDelayMs);
      return;
    }

    if (ResizeObserverImpl) {
      activeObserver = new ResizeObserverImpl((entries) => {
        for (const entry of entries) {
          if (entry.contentRect.width > 0 && entry.contentRect.height > 0 && !isMapReady?.()) {
            activeObserver?.disconnect();
            activeObserver = null;
            onReady?.();
          }
        }
      });
      activeObserver.observe(container);

      if (containerRect.width > 0 && containerRect.height > 0) {
        activeObserver.disconnect();
        activeObserver = null;
        onReady?.();
      }
      return;
    }

    onReady?.();
  };

  return {
    start: (delayMs = 50) => {
      setTimeout(initializeMap, delayMs);
    },
    stop: () => {
      stopped = true;
      try {
        activeObserver?.disconnect();
      } catch (_) {
        /* ignore */
      }
      activeObserver = null;
    }
  };
}

/** 安装救援目标/轨迹全局飞行函数（供列表 HTML 点击） */
export function installDashboardRescueFlyGlobals(getMap, win = typeof window !== 'undefined' ? window : null) {
  if (!win) return () => {};

  win.flyToRescueTarget = (lat, lng) => {
    const map = getMap?.();
    if (map && lat && lng) {
      const center = toMapLngLat(lng, lat, undefined, map) || [Number(lng), Number(lat)];
      try {
        map.stop?.();
      } catch (_) {
        /* ignore */
      }
      map.flyTo({ center, zoom: 16.7, duration: 2000, essential: true });
    }
  };
  win.flyToRescueTrack = (lat, lng) => {
    const map = getMap?.();
    if (map && lat && lng) {
      const center = toMapLngLat(lng, lat, undefined, map) || [Number(lng), Number(lat)];
      try {
        map.stop?.();
      } catch (_) {
        /* ignore */
      }
      map.flyTo({ center, zoom: 16.7, duration: 2000, essential: true });
    }
  };

  return () => {
    try {
      delete win.flyToRescueTarget;
      delete win.flyToRescueTrack;
    } catch (_) {
      /* ignore */
    }
  };
}
